import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link, useParams } from "react-router-dom";
import { GET_POSTS_BY_CATEGORY_NAME } from "../queries";
import { formatDate } from "../utils/format-date";
import Skeleton from "react-loading-skeleton";

const CategoryPage = () => {
  const { category } = useParams<{ category: string }>(); // Get the authorSlug from the URL

  var newCategory;
  switch (category) {
    case "wbball":
      newCategory = "Women's Basketball";
      break;
    case "bball":
      newCategory = "Men's Basketball";
      break;
    case "vball":
      newCategory = "Volleyball";
      break;
    case "cross-country":
      newCategory = "Cross Country";
      break;
    case "tf":
      newCategory = "Track and Field";
      break;
    case "soccer":
      newCategory = "Soccer";
      break;
    case "wrestling":
      newCategory = "Wrestling";
      break;
    default:
      newCategory = category;
  }
  //console.log("Category: ", category);
  //const [page, setPage] = useState(1);
  const perPage = 12;

  // Ensure the query runs only if authorSlug exists
  const { loading, error, data, fetchMore } = useQuery(
    GET_POSTS_BY_CATEGORY_NAME,
    {
      variables: {
        category: category,
        first: perPage,
        after: null,
      },
      skip: !category,
    }
  );

  function capitalizeFirstLetter(val: any) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }

  const [hasMore, setHasMore] = useState(true);

  // 2. Update the 'hasMore' state whenever the query data changes
  useEffect(() => {
    if (data) {
      setHasMore(data.posts.pageInfo.hasNextPage);
    }
  }, [data]); // This effect runs whenever 'data' is updated

  const loadMorePosts = async () => {
    if (!hasMore) {
      console.log("No more information to load.");
      return;
    }

    await fetchMore({
      variables: {
        // 'category' should be available in this scope
        after: data.posts.pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        // The new pageInfo from the fetchMoreResult will automatically update 'data',
        // which in turn triggers our useEffect to update the 'hasMore' state.
        return {
          posts: {
            ...fetchMoreResult.posts,
            nodes: [
              ...previousResult.posts.nodes,
              ...fetchMoreResult.posts.nodes,
            ],
          },
        };
      },
    });
  };

  // ... (handle loading and error states)
  if (error) return <p>Error: {error.message}</p>;

  //const skeletonPostIterate = 12;

  var posts = data?.posts?.nodes;

  // const handlePageChange = (newPage: number) => {
  //   setPage(newPage);
  // };

  if (posts?.length === 0)
    return <p>Could not find any posts for the category!</p>;

  return (
    <>
      <title>{newCategory}</title>
      <div className="mt-3 mx-3">
        <h1 className="text-center mb-8 text-3xl font-bold">
          {capitalizeFirstLetter(newCategory)}
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading
            ? [...Array(12)].map((_, i) => (
                <div key={i} className="post-article p-4 border rounded-lg shadow-sm">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="sm:w-5/12 flex-shrink-0">
                      <Skeleton
                        width="100%"
                        height="120px"
                        highlightColor="#0a5640"
                      />
                    </div>
                    <div className="sm:w-7/12">
                      <h5 className="mb-2">
                        <Skeleton
                          count={2}
                          height="1.25rem"
                          className="my-1"
                          highlightColor="#0a5640"
                        />
                      </h5>
                      <p>
                        <Skeleton
                          height="1rem"
                          width="60%"
                          highlightColor="#0a5640"
                        />
                      </p>
                    </div>
                  </div>
                </div>
              ))
            : posts.map((post: any) => (
                <Link
                  to={`/post/${post.slug}`}
                  className="no-underline text-inherit"
                  key={post.id}
                >
                  <div className="post-article p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow h-full">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="sm:w-5/12 flex-shrink-0">
                        <div className="image-wrapper">
                          <div className="skeleton-loader"></div>
                          {post.featuredImage?.node?.mediaDetails?.sizes && (
                            <img
                              src={
                                post.featuredImage.node.mediaDetails.sizes.find(
                                  (s: any) => s.name === "thumbnail"
                                )?.sourceUrl
                              }
                              alt="post"
                              className="real-img w-full h-auto object-cover rounded"
                              onLoad={(e) => {
                                const wrapper =
                                  e.currentTarget.closest(".image-wrapper");
                                if (wrapper) {
                                  const loader = wrapper.querySelector(
                                    ".skeleton-loader"
                                  ) as HTMLElement;
                                  if (loader) loader.style.display = "none";
                                  e.currentTarget.style.opacity = "1";
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="sm:w-7/12">
                        <h5
                          className="text-lg font-semibold mb-2"
                          dangerouslySetInnerHTML={{
                            __html: post.title,
                          }}
                        />
                        {post.author?.node?.name && (
                          <p className="text-sm mb-1">
                            <strong>Author:</strong> {post.author.node.name}
                          </p>
                        )}
                        {post.date && (
                          <p className="text-sm">
                            <strong>Date: </strong>
                            {formatDate(post.date)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
        </div>
        <div className="flex justify-center mt-12 mb-8">
          {hasMore ? (
            <button
              className="bg-[#0a5640] hover:bg-[#084533] text-white font-bold py-3 px-8 rounded-lg transition-colors"
              onClick={loadMorePosts}
            >
              Load More
            </button>
          ) : (
            <button
              className="bg-gray-400 text-white font-bold py-3 px-8 rounded-lg cursor-not-allowed"
              disabled
            >
              Load More
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
